import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { userService } from '../services/api.service';
import { motion } from 'framer-motion';
import { useDomain } from '../context/DomainContext';

const genderLabels = {
  'male': 'Masculino',
  'female': 'Femenino',
  'other': 'Otro'
};

const ConsentLink = styled.a`
  color: #ffd700;
  text-decoration: underline;
  margin-left: 4px;
  
  &:hover {
    color: #ffed4a;
  }
`;

const ProfileModal = ({ isOpen, onClose, user }) => {
  const { termsUrl, privacyUrl } = useDomain();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.userData?.name || '',
    phone: user?.userData?.phone || '',
    ageRange: user?.userData?.ageRange || '',
    gender: user?.userData?.gender || '',
    consents: {
      termsAndConditions: {
        accepted: user?.userData?.consents?.termsAndConditions?.accepted || false,
        acceptedAt: user?.userData?.consents?.termsAndConditions?.acceptedAt
      },
      privacyPolicy: {
        accepted: user?.userData?.consents?.privacyPolicy?.accepted || false,
        acceptedAt: user?.userData?.consents?.privacyPolicy?.acceptedAt
      },
      marketing: {
        accepted: user?.userData?.consents?.marketing?.accepted || false,
        acceptedAt: user?.userData?.consents?.marketing?.acceptedAt
      }
    }
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        consents: {
          ...prev.consents,
          [name]: {
            ...prev.consents[name],
            accepted: checked,
            acceptedAt: checked ? new Date().toISOString() : prev.consents[name].acceptedAt
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Actualizar solo edad y género
      await userService.updateUserProfile({
        ageRange: formData.ageRange,
        gender: formData.gender
      });

      // Actualizar consentimientos
      await userService.updateUserConsents(formData.consents);

      setIsEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error al actualizar datos:', error);
      toast.error('Error al actualizar el perfil');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGenderLabel = (genderValue) => {
    return genderLabels[genderValue] || 'No especificado';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <Header>
          <Title>Mi Perfil</Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </Header>

        {isEditing ? (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Nombre</Label>
              <Input
                type="text"
                value={formData.name}
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Teléfono</Label>
              <Input
                type="tel"
                value={formData.phone}
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Edad</Label>
              <Select
                name="ageRange"
                value={formData.ageRange}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona un rango</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55+">55+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Género</Label>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona género</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
                <option value="other">Otro</option>
              </Select>
            </FormGroup>

            <ConsentsSection>
              <Label>Consentimientos</Label>
              <CheckboxGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="termsAndConditions"
                    checked={formData.consents.termsAndConditions.accepted}
                    onChange={handleChange}
                    required
                  />
                  Acepto los
                  <ConsentLink 
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    términos y condiciones
                  </ConsentLink>
                </CheckboxLabel>
                <ConsentDate>
                  {formatDate(formData.consents.termsAndConditions.acceptedAt)}
                </ConsentDate>
              </CheckboxGroup>

              <CheckboxGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="privacyPolicy"
                    checked={formData.consents.privacyPolicy.accepted}
                    onChange={handleChange}
                    required
                  />
                  Acepto la
                  <ConsentLink 
                    href={privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    política de privacidad
                  </ConsentLink>
                </CheckboxLabel>
                <ConsentDate>
                  {formatDate(formData.consents.privacyPolicy.acceptedAt)}
                </ConsentDate>
              </CheckboxGroup>

              <CheckboxGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="marketing"
                    checked={formData.consents.marketing.accepted}
                    onChange={handleChange}
                  />
                  Acepto recibir comunicaciones comerciales
                </CheckboxLabel>
                <ConsentDate>
                  {formatDate(formData.consents.marketing.acceptedAt)}
                </ConsentDate>
              </CheckboxGroup>
            </ConsentsSection>

            <ButtonGroup>
              <CancelButton type="button" onClick={() => setIsEditing(false)}>
                Cancelar
              </CancelButton>
              <SaveButton type="submit">
                Guardar
              </SaveButton>
            </ButtonGroup>
          </Form>
        ) : (
          <>
            <UserInfo>
              <InfoItem>
                <Label>Email</Label>
                <Value>{formData.email}</Value>
              </InfoItem>

              <InfoItem>
                <Label>Nombre</Label>
                <Value>{formData.name || 'No especificado'}</Value>
              </InfoItem>

              <InfoItem>
                <Label>Teléfono</Label>
                <Value>{formData.phone || 'No especificado'}</Value>
              </InfoItem>

              <InfoItem>
                <Label>Edad</Label>
                <Value>{formData.ageRange || 'No especificado'}</Value>
              </InfoItem>

              <InfoItem>
                <Label>Género</Label>
                <Value>{getGenderLabel(formData.gender)}</Value>
              </InfoItem>
            </UserInfo>

            <ButtonGroup>
              <EditButton onClick={() => setIsEditing(true)}>
                Editar Perfil
              </EditButton>
            </ButtonGroup>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(76, 29, 149, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, 
    rgba(88, 28, 135, 0.95) 0%,
    rgba(67, 20, 102, 0.95) 100%
  );
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(76, 29, 149, 0.5);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
  padding-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: 'BaksoSapi', sans-serif;
  color: #ffd700;
  font-size: 1.5rem;
  margin: 0;
  letter-spacing: 1px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 215, 0, 0.8);
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: all 0.3s ease;

  &:hover {
    color: #ffd700;
    transform: rotate(90deg);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 215, 0, 0.8);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.1);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 215, 0, 0.4);
    background: rgba(255, 215, 0, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.1);
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 215, 0, 0.4);
    background: rgba(255, 215, 0, 0.15);
  }

  option {
    background: #4c1d95;
    color: #fff;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
`;

const Value = styled.div`
  color: #fff;
  font-size: 1.1rem;
`;

const ConsentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  cursor: pointer;
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const ConsentDate = styled.div`
  color: #999;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EditButton = styled(Button)`
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.2);

  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
`;

const CancelButton = styled(Button)`
  background: rgba(255, 69, 58, 0.2);
  color: #ff453a;
  border-color: rgba(255, 69, 58, 0.3);

  &:hover {
    background: rgba(255, 69, 58, 0.3);
  }
`;

const SaveButton = styled(Button)`
  background: rgba(52, 199, 89, 0.2);
  color: #34c759;
  border-color: rgba(52, 199, 89, 0.3);

  &:hover {
    background: rgba(52, 199, 89, 0.3);
  }
`;

export default ProfileModal; 